import { Settings } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DefaultImagesDialog } from '@/components/content/default-images-dialog';
import {
  DateRangeFilter,
  LIST_FILTER_DATE_RANGE_PRESETS,
} from '@/components/shared/date-range-filter';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { ContentSubnav } from '@/pages/content/content-subnav';
import { useContentPeriodStore } from '@/pages/content/content-period-store';

interface ContentPageHeaderProps {
  /** Tab-specific primary actions, rendered after the shared cog and date filter. */
  actions?: ReactNode;
}

export const ContentPageHeader = ({ actions }: ContentPageHeaderProps) => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const membership = useCurrentMembership();
  const canEdit = hasPermission(membership, PERMISSIONS.CONTENT_EDIT);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const dateRange = useContentPeriodStore((state) => state.dateRange);
  const setDateRange = useContentPeriodStore((state) => state.setDateRange);

  const rangeLabels = useMemo(
    () => ({
      today: tCommon('date.today'),
      yesterday: tCommon('date.yesterday'),
      thisWeek: tCommon('date.thisWeek'),
      lastWeek: tCommon('date.lastWeek'),
      thisMonth: tCommon('date.thisMonth'),
      lastMonth: tCommon('date.lastMonth'),
      thisQuarter: tCommon('date.thisQuarter'),
      thisYear: tCommon('date.thisYear'),
      lastYear: tCommon('date.lastYear'),
      allTime: tCommon('date.allTime'),
      custom: tCommon('date.custom'),
      from: t('overview.dateFrom'),
      to: t('overview.dateTo'),
    }),
    [t, tCommon],
  );

  return (
    <>
      <PageHeader
        title={t('title')}
        description={t('description')}
        tipId="content.scheduleTimezone"
        headerActions={
          <div className="flex items-center gap-2">
            {canEdit ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={t('defaultImages.open')}
                onClick={() => setDefaultsOpen(true)}
              >
                <Settings className="size-4" aria-hidden />
              </Button>
            ) : null}
            <DateRangeFilter
              value={dateRange}
              onChange={setDateRange}
              labels={rangeLabels}
              presets={LIST_FILTER_DATE_RANGE_PRESETS}
            />
            {actions}
          </div>
        }
      />
      <DefaultImagesDialog open={defaultsOpen} onOpenChange={setDefaultsOpen} />
      <ContentSubnav />
    </>
  );
};
